import React, { useState } from 'react';
import { CalendarClock, Check, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { publicNameOf } from '../utils/brandName';
import { formatLongDate, restoreAccount, type RestoredSubscription } from '../services/accountService';
import { ExportRow } from '../components/workspace/settings/AccountPanel';

// What a brand owner sees on signing in while their account is scheduled for
// deletion (#452, owner 2026-11-02: 30 days, restore by signing in). The rest
// of the app is closed: the API refuses everything but this screen's calls.

const AccountPendingDeletion: React.FC = () => {
  const { userProfile, refetchProfile, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState<RestoredSubscription | null>(null);
  const deletion = userProfile?.deletion;
  const name = publicNameOf(userProfile);

  const restore = async () => {
    setBusy(true);
    setError(null);
    try {
      setRestored(await restoreAccount());
    } catch (e: any) {
      setError(e?.message ?? "We couldn't restore the account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const card = 'w-full max-w-xl bg-white dark:bg-night-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-sm p-6 sm:p-8';

  if (restored) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-4 bg-gray-50 dark:bg-night-950">
        <img src="/BasiQR-logo.svg" alt="BasiQR" className="h-8 w-auto dark:brightness-0 dark:invert" />
        <div className={card} role="status">
          <Check size={28} className="text-william-600 dark:text-william-300" aria-hidden="true" />
          <h1 className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">{name} is back</h1>
          <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <li>Your team can sign in again, and your line sheets open again.</li>
            <li>API keys and connected apps were revoked when you asked to delete. Create new ones in Settings.</li>
            {restored === 'resumed' && <li>Your plan renews again as before.</li>}
            {restored === 'ended' && <li>Your plan ended while the account was closing. Choose a plan in Billing to subscribe again.</li>}
          </ul>
          <button
            type="button"
            onClick={() => refetchProfile()}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-william-600 dark:bg-william-300 dark:text-night-950 text-white hover:bg-william-700 dark:hover:bg-william-200 transition-colors"
          >
            Go to your workspace
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-4 bg-gray-50 dark:bg-night-950">
      <img src="/BasiQR-logo.svg" alt="BasiQR" className="h-8 w-auto dark:brightness-0 dark:invert" />
      <div className={card}>
        <CalendarClock size={28} className="text-red-600 dark:text-red-400" aria-hidden="true" />
        <h1 className="mt-3 text-2xl font-bold text-gray-900 dark:text-white text-balance">
          {name}'s account will be deleted on {deletion ? formatLongDate(deletion.purgeAfter) : 'its deletion date'}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          You asked to delete it{deletion ? ` on ${formatLongDate(deletion.requestedAt)}` : ''}. Until then:
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc pl-5">
          <li>Your product pages stay online, and every printed code keeps working.</li>
          <li>Your team, API keys, connected apps and line sheets are switched off.</li>
          <li>On that day, your sign-in, team and personal data are erased for good.</li>
        </ul>

        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={restore}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-william-600 dark:bg-william-300 dark:text-night-950 text-white hover:bg-william-700 dark:hover:bg-william-200 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={16} aria-hidden="true" />}
            Restore my account
          </button>
          <button
            type="button"
            onClick={() => logout()}
            disabled={busy}
            className="px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Sign out
          </button>
        </div>

        <div className="mt-8 border-t border-gray-200 dark:border-white/10 pt-6">
          <ExportRow isMember={false} />
        </div>
      </div>
    </main>
  );
};

export default AccountPendingDeletion;
